/**
 * Service for CreativeElements ("components" / "elements") and their tags.
 */
import BaseService from './baseService';
import { dxApiEndpoints } from '@/constants/dxApiEndpoints';
import { CreativeElementStatus } from '@/types/creativeElement';
import type {
  CreativeElement,
  CreativeElementApiRequest,
  CreativeElementLayerData,
  CreativeElementListResponse,
  CreativeElementMeta,
  CreativeElementResponse,
  CreativeElementSaveRequest,
  CreativeElementTagResponse,
  CreativeElementTagListResponse,
  CreateCategoryRequest,
  SizeConfiguration,
  ListCategoriesParams,
  ListComponentsParams
} from '@/types/creativeElement';

/** Mirrors the API's `SortType` / `CreativeElementSortBy` enums. */
const CREATIVE_ELEMENT_SORT_DESCENDING = 2;
const CREATIVE_ELEMENT_SORT_BY_CREATED_AT = 5;

/** Tolerant parse: a malformed or absent column shouldn't break the panel. */
function parseJson<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

const emptyLayerData = (): CreativeElementLayerData => ({
  refTree: {},
  childOrder: []
});

function toCreativeElement(row: CreativeElementResponse): CreativeElement {
  return {
    id: row.id,
    name: row.name,
    tag: row.tag?.id ?? 0,
    layerData: parseJson<CreativeElementLayerData>(
      row.liteLayerData,
      emptyLayerData()
    ),
    sizeConfigurations: parseJson<SizeConfiguration[]>(
      row.sizeConfigurations,
      []
    ),
    status: row.status,
    isDeletedComponent: row.status === CreativeElementStatus.Deleted,
    thumbnailUrl: row.thumbnailUrl,
    version: row.version
  };
}

function toApiRequest(
  request: CreativeElementSaveRequest
): CreativeElementApiRequest {
  return {
    name: request.name,
    tagId: request.tag,
    liteLayerData: request.liteLayerData,
    sizeConfigurations: request.sizeConfigurations,
    imageFile: request.imageFile
  };
}

class CreativeElementService extends BaseService {
  /** Creates a component. Returns the persisted record. */
  saveComponent = async (
    businessProfileId: number,
    request: CreativeElementSaveRequest
  ): Promise<CreativeElement> => {
    const url = dxApiEndpoints.SaveCreativeElement(businessProfileId);
    const { data } = await this.apiClient.post<{
      data: CreativeElementResponse;
    }>(url, toApiRequest(request));
    return toCreativeElement(data);
  };

  /**
   * Fetches one component by id. Soft-deleted rows come back with full data
   * and `isDeletedComponent: true` so referencing templates can inflate once
   * more and detach on load.
   */
  getComponent = async (
    businessProfileId: number,
    id: number
  ): Promise<CreativeElement> => {
    const url = dxApiEndpoints.GetCreativeElement(businessProfileId, id);
    const { data } = await this.apiClient.get<{
      data: CreativeElementResponse;
    }>(url, { isLite: true });
    return toCreativeElement(data);
  };

  /**
   * Current version + status for a set of components, without their layer data.
   * Feeds the load-time version reconcile, which only compares integers.
   *
   * Soft-deleted rows come back with `status: Deleted`. Ids that resolve to
   * nothing — hard-deleted — are simply
   * absent from the response; callers treat a missing id as deleted.
   */
  listComponentMeta = async (
    businessProfileId: number,
    ids: number[]
  ): Promise<CreativeElementMeta[]> => {
    if (ids.length === 0) return [];

    const url = dxApiEndpoints.GetCreativeElementMeta(businessProfileId);
    const { data } = await this.apiClient.get<{
      data: CreativeElementMeta[] | null;
    }>(url, { ids });
    return data ?? [];
  };

  /** Lists components page by page, optionally filtered by tag. */
  listComponents = async (
    businessProfileId: number,
    params: ListComponentsParams
  ): Promise<CreativeElementListResponse> => {
    const url = dxApiEndpoints.GetCreativeElements(businessProfileId);
    const { data, totalElements } = await this.apiClient.get<{
      data: CreativeElementResponse[];
      totalElements: number;
    }>(url, {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      sort: CREATIVE_ELEMENT_SORT_DESCENDING,
      sortBy: CREATIVE_ELEMENT_SORT_BY_CREATED_AT,
      statuses: [CreativeElementStatus.Active],
      isLite: true,
      ...(params.tag == null ? {} : { tagIds: [params.tag] }),
      // The API rejects blank keywords, so an empty query sends none.
      ...(params.search ? { searchKeywords: [params.search] } : {})
    });
    return {
      items: (data ?? []).map(toCreativeElement),
      totalCount: totalElements ?? 0
    };
  };

  /** Updates a component in place. */
  updateComponent = async (
    businessProfileId: number,
    id: number,
    request: CreativeElementSaveRequest
  ): Promise<CreativeElement> => {
    const url = dxApiEndpoints.UpdateCreativeElement(businessProfileId, id);
    const { data } = await this.apiClient.patch<{
      data: CreativeElementResponse;
    }>(url, toApiRequest(request));
    return toCreativeElement(data);
  };

  /** Renames a component. Leaves layer data and version untouched. */
  renameComponent = async (
    businessProfileId: number,
    id: number,
    name: string
  ): Promise<void> => {
    const url = dxApiEndpoints.RenameCreativeElement(businessProfileId, id);
    await this.apiClient.patch(url, { name });
  };

  /** Soft-deletes a component (status -> Deleted). */
  deleteComponent = async (
    businessProfileId: number,
    id: number
  ): Promise<void> => {
    const url = dxApiEndpoints.DeleteCreativeElement(businessProfileId, id);
    await this.apiClient.delete(url);
  };

  /**
   * Lists the lite element tags page by page, most recently updated first.
   */
  listCategories = async (
    businessProfileId: number,
    params: ListCategoriesParams
  ): Promise<CreativeElementTagListResponse> => {
    const url = dxApiEndpoints.GetCreativeElementTags(businessProfileId);
    const { data, totalCategories } = await this.apiClient.get<{
      data: CreativeElementTagResponse[];
      totalCategories: number;
    }>(url, {
      isLite: true,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize
    });
    return { items: data ?? [], totalCount: totalCategories ?? 0 };
  };

  /**
   * Searches tags by name prefix.
   */
  searchCategories = async (
    businessProfileId: number,
    query: string
  ): Promise<CreativeElementTagResponse[]> => {
    const url = dxApiEndpoints.SearchCreativeElementTags(businessProfileId);
    const { data } = await this.apiClient.get<{
      data: CreativeElementTagResponse[];
    }>(url, { query, isLite: true });
    return data ?? [];
  };

  /**
   * Creates a user tag.
   */
  createCategory = async (
    businessProfileId: number,
    request: CreateCategoryRequest
  ): Promise<CreativeElementTagResponse> => {
    const url = dxApiEndpoints.CreateCreativeElementTag(businessProfileId);
    const { data } = await this.apiClient.post<{
      data: CreativeElementTagResponse;
    }>(url, {
      name: request.name,
      imageFile: request.imageFile,
      isLite: true
    });
    return data;
  };
}

export default new CreativeElementService();
